import type { StateCreator } from 'zustand';
import {
  expansionAdvisorApi,
  type PoolDocument,
  type ClusterDetail,
  type ExpansionSuggestion,
  type ExpansionContext,
  type ChooseResponse,
} from '../services/expansionAdvisorApi';

export interface ExpansionAdvisorSlice {
  expansionPool: PoolDocument[];
  expansionPoolLoading: boolean;
  expansionContext: ExpansionContext | null;
  expansionActiveDocId: number | null;
  expansionActiveDocInsights: ClusterDetail[] | null;
  expansionInsightsLoading: boolean;
  expansionRunId: number | null;
  expansionSuggestions: ExpansionSuggestion[] | null;
  expansionWarnings: string[];
  expansionRunLoading: boolean;
  expansionError: string | null;

  loadExpansionPool: () => Promise<void>;
  uploadExpansionFiles: (files: File[]) => Promise<unknown[]>;
  deleteExpansionDoc: (docId: number) => Promise<void>;
  retryExpansionTagging: (docId: number) => Promise<void>;
  loadExpansionDocInsights: (docId: number) => Promise<void>;
  updateExpansionInsightScope: (
    insightId: number,
    scope: 'global' | 'specific' | 'unallocated',
    strategyIds: number[]
  ) => Promise<void>;
  bulkUpdateExpansionScope: (
    insightIds: number[],
    scope: 'global' | 'specific' | 'unallocated',
    strategyIds: number[]
  ) => Promise<number>;
  loadExpansionContext: () => Promise<void>;
  generateExpansionSuggestions: (focusNiches?: string[]) => Promise<void>;
  chooseExpansionSuggestion: (suggestionIndex: number) => Promise<ChooseResponse>;
  clearExpansionActiveDoc: () => void;
}

export const createExpansionAdvisorSlice: StateCreator<ExpansionAdvisorSlice> = (set, get) => ({
  expansionPool: [],
  expansionPoolLoading: false,
  expansionContext: null,
  expansionActiveDocId: null,
  expansionActiveDocInsights: null,
  expansionInsightsLoading: false,
  expansionRunId: null,
  expansionSuggestions: null,
  expansionWarnings: [],
  expansionRunLoading: false,
  expansionError: null,

  async loadExpansionPool() {
    set({ expansionPoolLoading: true });
    try {
      const pool = await expansionAdvisorApi.listPool();
      set({ expansionPool: pool, expansionPoolLoading: false });
    } catch (e) {
      set({
        expansionPoolLoading: false,
        expansionError: (e as Error).message,
      });
    }
  },

  async uploadExpansionFiles(files) {
    const results = await expansionAdvisorApi.uploadToPool(files);
    await get().loadExpansionPool();
    return results as unknown[];
  },

  async deleteExpansionDoc(docId) {
    await expansionAdvisorApi.deleteFromPool(docId);
    set((s) => ({
      expansionPool: s.expansionPool.filter((d) => d.id !== docId),
      expansionActiveDocId: s.expansionActiveDocId === docId ? null : s.expansionActiveDocId,
      expansionActiveDocInsights: s.expansionActiveDocId === docId ? null : s.expansionActiveDocInsights,
    }));
  },

  async retryExpansionTagging(docId) {
    await expansionAdvisorApi.retryTagging(docId);
    await get().loadExpansionPool();
  },

  async loadExpansionDocInsights(docId) {
    set({ expansionActiveDocId: docId, expansionInsightsLoading: true });
    try {
      const insights = await expansionAdvisorApi.listDocInsights(docId);
      set({ expansionActiveDocInsights: insights, expansionInsightsLoading: false });
    } catch (e) {
      set({
        expansionInsightsLoading: false,
        expansionError: (e as Error).message,
      });
    }
  },

  async updateExpansionInsightScope(insightId, scope, strategyIds) {
    await expansionAdvisorApi.updateInsightScope(insightId, {
      scope_type: scope,
      strategy_ids: strategyIds,
    });
    const docId = get().expansionActiveDocId;
    if (docId != null) {
      await get().loadExpansionDocInsights(docId);
    }
  },

  async bulkUpdateExpansionScope(insightIds, scope, strategyIds) {
    const res = await expansionAdvisorApi.bulkUpdateScope({
      insight_ids: insightIds,
      scope_type: scope,
      strategy_ids: strategyIds,
    });
    const docId = get().expansionActiveDocId;
    if (docId != null) {
      await get().loadExpansionDocInsights(docId);
    }
    return res.updated;
  },

  async loadExpansionContext() {
    const ctx = await expansionAdvisorApi.getContext();
    set({ expansionContext: ctx });
  },

  async generateExpansionSuggestions(focusNiches = []) {
    set({ expansionRunLoading: true, expansionError: null, expansionWarnings: [] });
    try {
      const res = await expansionAdvisorApi.generateSuggestions(focusNiches);
      set({
        expansionRunId: res.run_id,
        expansionSuggestions: res.suggestions,
        expansionWarnings: res.warnings || [],
        expansionRunLoading: false,
      });
    } catch (e) {
      set({
        expansionRunLoading: false,
        expansionError: (e as Error).message,
      });
    }
  },

  async chooseExpansionSuggestion(suggestionIndex) {
    const runId = get().expansionRunId;
    if (runId == null) {
      throw new Error('no active expansion run');
    }
    return expansionAdvisorApi.chooseSuggestion(runId, suggestionIndex);
  },

  clearExpansionActiveDoc() {
    set({ expansionActiveDocId: null, expansionActiveDocInsights: null });
  },
});
